import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/** Returns true if the current authenticated user has the `admin` role.
 *  Respects the "View as Mod" preview toggle — when active, returns false
 *  so admins can test the mod experience.
 */
export const useIsAdmin = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const check = async (cancelledRef: { current: boolean }) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      if (!cancelledRef.current) { setIsAdmin(false); setLoading(false); }
      return;
    }
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .eq("role", "admin")
      .maybeSingle();
    const realAdmin = !!data;
    const viewAsMod = typeof window !== "undefined" && localStorage.getItem("viewAsMod") === "1" && realAdmin;
    if (!cancelledRef.current) {
      setIsAdmin(viewAsMod ? false : realAdmin);
      setLoading(false);
    }
  };

  useEffect(() => {
    const cancelledRef = { current: false };
    check(cancelledRef);

    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      setLoading(true);
      check(cancelledRef);
    });

    const onStorage = (e: StorageEvent) => { if (e.key === "viewAsMod") check(cancelledRef); };
    const onCustom = () => check(cancelledRef);
    window.addEventListener("storage", onStorage);
    window.addEventListener("viewAsMod-changed", onCustom);

    return () => {
      cancelledRef.current = true;
      sub.subscription.unsubscribe();
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("viewAsMod-changed", onCustom);
    };
  }, []);

  return { isAdmin, loading };
};
